/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type {
  CommunityBroadcast,
  UseRequestCommunityBroadcastPostVariables,
} from "@packages/sdk";
import {
  CommunityType,
  useRequestCommunitiesAdminCommunity,
  useRequestCommunitiesChildren,
  useRequestCommunityBroadcast,
  useRequestMe,
  zodRequiredString,
} from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  ArrowLeftIcon,
  Button,
  CloseIcon,
  colors,
  cssMediaQueryMinWidthTablet,
  EyeIcon,
  Form,
  FormField,
  FullscreenModal,
  IconButton,
  Journal1Icon,
  numericPercentages,
  PullDownBar,
  radius,
  ReactIcon,
  ScrollArea,
  spacing,
  standardSchemaResolver,
  Text,
  TextArea,
  TimeIcon,
  useAlert,
  useConfirm,
  useForm,
  useIsDesktopViewport,
  useModal,
  zIndices,
} from "@packages/ui";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { EmojiPickerIconButton } from "@ui/emoji";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { getBroadcastErrorDescription } from "../../../../../lib/utils/hallowAdminDashboard/broadcasts";
import { BroadcastCreateSentToList } from "./BroadcastCreateSentToList";
import type { BroadcastScheduleModalResult } from "./BroadcastScheduleModal";
import { BroadcastScheduleModal } from "./BroadcastScheduleModal";

const cssButton: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: colors.transparent,
  border: "none",
  color: theme.semanticColors.onOverlayHighest,
  cursor: "pointer",
});

const cssButtonDisabled: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.onOverlayMedHigh,
});

const cssContent: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  borderRadius: radius.l,
  height: "100%",
  left: numericPercentages[50],
  margin: spacing.m,
  maxHeight: 640,
  maxWidth: 640,
  overflowX: "hidden",
  overflowY: "auto",
  position: "fixed",
  top: numericPercentages[50],
  transform: "translate(-50%, -50%)",
  width: spacing.full,
  zIndex: zIndices.modalContent,
});

const cssContentContainer: Interpolation = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  position: "relative",
  overflow: "hidden",
};

const cssContentDrawer: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  left: "100%",
  maxHeight: 640,
  maxWidth: 640,
  overflow: "auto",
  position: "absolute",
  top: 0,
  transition: "left 125ms ease-in",
  width: "100%",
  zIndex: zIndices.overlayOverModal,
});

const cssContentDrawerOpen: Interpolation = {
  left: 0,
};

const cssFooter: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.onOverlayBackground,
  bottom: 0,
  display: "flex",
  gap: spacing.s,
  justifyContent: "space-between",
  paddingBottom: spacing.m,
  paddingLeft: spacing.xl,
  paddingRight: spacing.xl,
  paddingTop: spacing.m,
  position: "sticky",
});

const cssFooterActions: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  minWidth: 0,
};

const cssForm: Interpolation = {
  flexGrow: 1,
  paddingInline: spacing.m,
  paddingTop: spacing.s,
  height: "100%",
  [cssMediaQueryMinWidthTablet]: {
    paddingInline: spacing.xl,
    paddingTop: undefined,
  },
};

const cssHeader: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.onOverlayBackground,
  display: "flex",
  gap: spacing.s,
  paddingBottom: spacing.m,
  paddingLeft: spacing.xl,
  paddingRight: spacing.xl,
  paddingTop: spacing.m,
  position: "sticky",
  top: 0,
});

const cssHeaderContentDrawer: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  paddingTop: spacing.l,
  paddingBottom: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    paddingTop: spacing.s,
    paddingBottom: spacing.s,
  },
};

const cssHeaderContentForm: Interpolation = {
  justifyContent: "flex-end",
};

const cssHeaderIconContainer: Interpolation = {
  width: 40,
};

const cssIconButton: Interpolation = {
  borderRadius: radius.s,
};

const cssOverlay: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.higherContrastMed,
  inset: spacing.none,
  position: "fixed",
  zIndex: zIndices.overlayOverMediaPlayer,
});

const cssPlaceholder: Interpolation = {
  left: 0,
  top: 0,
};

/**
 * Host for the emoji popover, which has to live inside the dialog's own content
 * to survive being used (see `popoverContainer` below). Taken out of flow so it
 * contributes neither a box nor a flex gap to the layout it is dropped into;
 * the popover it receives positions itself, so where this sits does not matter.
 */
const cssPopoverContainer: Interpolation = {
  position: "absolute",
};

const cssTextAreaContainer: Interpolation = {
  height: "100%",
};

const cssTextArea: Interpolation = {
  border: "none",
  height: "100%",
  marginTop: spacing.none,
  padding: 0,
};

const cssTitle: Interpolation = {
  flexGrow: 1,
  textAlign: "center",
};

const cssMobileSendToDrawerContent: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayBackground,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  left: 0,
  maxHeight: "100dvh",
  opacity: 0,
  pointerEvents: "none",
  position: "absolute",
  right: 0,
  top: 0,
  transform: "translateY(100%)",
  transition:
    "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms ease",
  zIndex: zIndices.modalOverModal,
});

const cssMobileSendToDrawerContentOpen: Interpolation = {
  opacity: 1,
  pointerEvents: "auto",
  transform: "translateY(0)",
};

const cssMobileHeader: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
};

const cssMobileContent: Interpolation = {
  padding: spacing.m,
};

const cssMobileSubmitButton: Interpolation = {
  justifyContent: "center",
};

const cssMobileGroupsListButton: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});

const cssMobileGroupsListButtonContainer: Interpolation = {
  display: "flex",
  justifyContent: "space-between",
};

const cssMobileContentDrawerHeader: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  paddingBottom: spacing.s,
  paddingInline: spacing.l,
  paddingTop: spacing.m,
};

const cssMobileFooter: Interpolation = {
  alignSelf: "flex-end",
  paddingInline: spacing.l,
  paddingTop: spacing.m,
  paddingBottom: spacing.m,
  width: spacing.full,
};

const cssMobileContentDrawerScrollAreaViewport: Interpolation = {
  height: `calc(100dvh - ${spacing.xxxl} - ${spacing.xxl})`,
};

const BROADCAST_MAX_LENGTH = 500;

type BroadcastCreateModalProps = Dialog.DialogProps & {
  /**
   * Set to edit an existing scheduled broadcast rather than compose a new one.
   * `PUT` replaces content, recipients and schedule outright, so the same form
   * covers both and the design uses one modal for them.
   */
  broadcast?: CommunityBroadcast;
  communityId: number;
  handleToggleOpen: () => void;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  /** `isScheduled` says whether the broadcast was held rather than sent. */
  onSuccess?: (isScheduled: boolean) => void;
};

export const BroadcastCreateModal = ({
  broadcast,
  communityId,
  handleToggleOpen,
  open,
  setOpen,
  onSuccess,
  ...props
}: BroadcastCreateModalProps) => {
  const { track } = useAnalytics();
  const isEditing = Boolean(broadcast);
  const [drawerOpen, setDrawerOpen] = useState(false);

  /**
   * The emoji popover portals here rather than to `document.body`. The compose
   * modal is a modal Radix dialog, which traps focus to its content, blocks
   * scrolling outside it and treats pointer events landing outside it as a
   * dismissal — so a popover in `body` reads as "outside the modal" and is
   * closed as soon as it is scrolled or paged. Held in state, not a ref, so the
   * popover re-renders once the node exists.
   */
  const [popoverContainer, setPopoverContainer] =
    useState<HTMLDivElement | null>(null);

  /** The recipients the broadcast already has, when editing one. */
  const broadcastCommunityIds = useMemo(
    () =>
      (broadcast?.broadcasted_to_communities ?? [])
        .map(({ id }) => id)
        .filter((id): id is number => id != null),
    [broadcast],
  );

  const { query: queryMe } = useRequestMe();

  const { query: queryCommunitiesAdminCommunity } =
    useRequestCommunitiesAdminCommunity({ id: communityId });
  const isNetwork =
    queryCommunitiesAdminCommunity.data?.type === CommunityType.network;

  const [communityIds, setCommunityIds] = useState<number[]>(
    broadcastCommunityIds,
  );
  const [scheduledAt, setScheduledAt] = useState<Date | null>(
    broadcast?.scheduled_at ? new Date(broadcast.scheduled_at) : null,
  );
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  /** Already seeded when editing, so the select-everything default is skipped. */
  const [isCommunityIdsSet, setIsCommunityIdsSet] = useState(isEditing);

  const alert = useAlert();
  const scheduleModal = useModal(BroadcastScheduleModal);
  const { mutationPost, mutationPut } = useRequestCommunityBroadcast({
    communityId,
  });
  const { infiniteQuery: communitiesChildrenBasicOrSmallGroupInfiniteQuery } =
    useRequestCommunitiesChildren({
      id: communityId,
      type: isNetwork ? CommunityType.basic : CommunityType.small_group,
    });
  const t = useTranslations();
  const isDesktop = useIsDesktopViewport();

  const createBroadcastFormSchema = z.object({
    content: zodRequiredString(
      t("error_any_required", {
        0: t("dashboard_broadcast_content_field_name"),
      }),
    ).max(
      BROADCAST_MAX_LENGTH,
      t("dynamic_words_cannot_exceed_characters", { 0: BROADCAST_MAX_LENGTH }),
    ),
  });

  const dialogDiscard = useConfirm({
    cancelButton: t("general_word_cancel"),
    description: () => t("dashboard_broadcast_discard_description"),
    primaryButton: (onClick) => (
      <Button
        variant="color"
        color={colors.red60}
        onClick={() => {
          handleConfirmDiscard();
          onClick();
        }}
      >
        {t("dashboard_broadcast_discard")}
      </Button>
    ),
    title: () => t("giving_donation_unsaved_changes_title"),
  });

  const form = useForm<
    Pick<UseRequestCommunityBroadcastPostVariables, "content">
  >({
    defaultValues: {
      content: broadcast?.content ?? "",
    },
    resolver: standardSchemaResolver(createBroadcastFormSchema),
  });

  const handleClose = async () => {
    if (open) {
      if (isUpdated) {
        const discard = await dialogDiscard.show();
        if (discard) handleDiscard();
      } else handleDiscard();
    }
  };

  const handleConfirmDiscard = () => {
    track({
      event: HallowAnalyticsEvent.TappedDiscardBroadcastMessage,
    });
    handleDiscard();
  };

  const handleDiscard = () => {
    setDrawerOpen(false);
    setIsCommunityIdsSet(isEditing);
    setCommunityIds(broadcastCommunityIds);
    setScheduledAt(
      broadcast?.scheduled_at ? new Date(broadcast.scheduled_at) : null,
    );
    form.reset();
    handleToggleOpen();
  };

  /**
   * Inserts at the caret rather than appending, so an emoji picked part-way
   * through a message lands where the admin left off. `EmojiPicker`'s own
   * `inputRef` insertion cannot do this for us: it works through Range and
   * Selection, which do not reach inside a `textarea`'s text. Going through
   * `setValue` also keeps react-hook-form's dirty and valid state in step,
   * which writing to the node directly would bypass.
   */
  const handleEmojiInput = (emoji: string) => {
    const content = form.getValues().content ?? "";
    const textArea = textAreaRef.current;
    // The picker steals focus, so these still hold the caret as the admin left
    // it. They fall back to the end for a field that has never been focused.
    const selectionStart = textArea?.selectionStart ?? content.length;
    const selectionEnd = textArea?.selectionEnd ?? content.length;
    const updated = `${content.slice(0, selectionStart)}${emoji}${content.slice(
      selectionEnd,
    )}`;

    if (updated.length > BROADCAST_MAX_LENGTH) return;

    form.setValue("content", updated, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    // Re-rendering with the new value drops the caret to the end, so put it
    // back after the emoji once React has written the value out.
    const caret = selectionStart + emoji.length;
    requestAnimationFrame(() => textArea?.setSelectionRange(caret, caret));
  };

  const handleEmojiPicker = () => {
    track({
      event: HallowAnalyticsEvent.TappedBroadcastMessageEmojiIcon,
      properties: { community_id: communityId },
    });
  };

  const handleSchedule = async () => {
    const result = (await scheduleModal.show({
      communityId,
      scheduledAt,
    })) as BroadcastScheduleModalResult | undefined;

    if (result) setScheduledAt(result.scheduledAt);
  };

  const handleGroupChats = () => {
    track({ event: HallowAnalyticsEvent.TappedBroadcastMessageEditorGroups });
    handleToggleDrawerOpen();
  };

  const handleSubmit = async (
    data: Pick<UseRequestCommunityBroadcastPostVariables, "content">,
  ) => {
    try {
      if (broadcast) {
        await mutationPut.mutateAsync({
          broadcastId: broadcast.id,
          broadcasted_to_community_ids: communityIds,
          // `null` clears the schedule, which the endpoint sends on.
          scheduled_at: scheduledAt ? scheduledAt.toISOString() : null,
          ...data,
        });
      } else {
        await mutationPost.mutateAsync({
          broadcasted_to_community_ids: communityIds,
          // Omitted entirely when unscheduled, which is the send-now path.
          ...(scheduledAt ? { scheduled_at: scheduledAt.toISOString() } : {}),
          ...data,
        });
        // Defined as firing after the POST resolves, so an edit is not one.
        track({
          event: HallowAnalyticsEvent.TappedSendBroadcastMessage,
          properties: {
            group_type: isNetwork ? "network" : "parish",
            sender_first_name: queryMe.data?.name,
            sender_user_id: queryMe.data?.id,
          },
        });
      }
      handleToggleOpen();
      handleDiscard();
      onSuccess?.(Boolean(scheduledAt));
    } catch (error) {
      /**
       * The picker cannot offer a past time, but a draft left open long enough
       * can outlive the slot it chose. The schedule is left in place: clearing
       * it would turn the next tap of Send into an immediate broadcast.
       */
      alert.show({
        title: t("general_word_error"),
        description: getBroadcastErrorDescription(error, t),
      });
    }
  };

  const handleToggleDrawerOpen = () => setDrawerOpen(!drawerOpen);

  const handleTrigger = () => {
    track({
      event: HallowAnalyticsEvent.TappedWriteBroadcastMessage,
      properties: {
        button: "icon",
        group_type: isNetwork ? "network" : "parish",
      },
    });
    handleToggleOpen();
  };

  const isDisabled =
    !form.formState?.isDirty ||
    !form.formState?.isValid ||
    form.formState?.isSubmitting ||
    communityIds.length === 0;

  const isUpdated = isEditing
    ? form.getValues().content !== broadcast.content ||
      scheduledAt?.toISOString() !==
        (broadcast.scheduled_at
          ? new Date(broadcast.scheduled_at).toISOString()
          : undefined) ||
      JSON.stringify(communityIds) !== JSON.stringify(broadcastCommunityIds)
    : form.getValues().content !== "" ||
      scheduledAt !== null ||
      JSON.stringify(communityIds) !==
        JSON.stringify(
          queryCommunitiesAdminCommunity.data?.type !== CommunityType.network
            ? [
                communityId,
                ...(communitiesChildrenBasicOrSmallGroupInfiniteQuery.data
                  ?.pages[0]?.all_children_community_ids || []),
              ]
            : communitiesChildrenBasicOrSmallGroupInfiniteQuery.data?.pages[0]
                ?.all_children_community_ids,
        );

  useEffect(() => {
    if (
      !isCommunityIdsSet &&
      queryCommunitiesAdminCommunity.data?.type &&
      communitiesChildrenBasicOrSmallGroupInfiniteQuery.data?.pages[0]
        ?.all_children_community_ids
    ) {
      if (queryCommunitiesAdminCommunity.data?.type !== CommunityType.network)
        setCommunityIds([
          communityId,
          ...communitiesChildrenBasicOrSmallGroupInfiniteQuery.data?.pages[0]
            ?.all_children_community_ids,
        ]);
      else
        setCommunityIds(
          communitiesChildrenBasicOrSmallGroupInfiniteQuery.data?.pages[0]
            ?.all_children_community_ids,
        );
      setIsCommunityIdsSet(true);
    }
  }, [
    isCommunityIdsSet,
    queryCommunitiesAdminCommunity.data,
    communitiesChildrenBasicOrSmallGroupInfiniteQuery.data,
  ]);

  const emojiPicker = (
    <EmojiPickerIconButton
      buttonProps={{
        icon: <ReactIcon />,
        onClick: handleEmojiPicker,
        size: "s",
        variant: "neutral",
      }}
      pickerProps={{ onInput: handleEmojiInput }}
      contentProps={{
        /**
         * Opens leftward from the button. Radix pins `shift` to the main axis
         * only, so for a `top` side nothing corrects a horizontal overflow and
         * `align` is the whole of the horizontal placement — and the button
         * sits in the footer's right-hand cluster, with far less room to its
         * right than the picker needs.
         */
        align: "end",
        container: popoverContainer,
        side: "top",
        /**
         * Belt and braces for narrow viewports, where even opening leftward can
         * run past the modal: the picker gives up columns rather than being cut
         * off by the modal's `overflow`. No-ops wherever the picker already
         * fits, which is everywhere above a phone.
         */
        style: { maxWidth: "var(--radix-popover-content-available-width)" },
      }}
    />
  );

  /**
   * Rendered inside whichever dialog content is in play, and handed to the
   * popover above as its portal target.
   */
  const popoverContainerHost = (
    <div css={cssPopoverContainer} ref={setPopoverContainer} />
  );

  const scheduleButton = (
    <IconButton
      icon={<TimeIcon />}
      onClick={handleSchedule}
      size="s"
      variant="neutral"
    />
  );

  /**
   * The submit button is what tells the admin a schedule is set — the design
   * has no separate date label in the footer, it swaps Send for Schedule.
   */
  const submitLabel = scheduledAt
    ? "dashboard_broadcast_schedule"
    : "button_description_send";

  const trigger = (
    <IconButton
      icon={<Journal1Icon />}
      onClick={handleTrigger}
      css={cssIconButton}
      variant="transparentPrimary"
    />
  );

  const broadcastCreateForm = (
    <Form
      formProps={{
        id: "dashboard-broadcast-create-form",
        onSubmit: form.handleSubmit(handleSubmit),
      }}
      formCSS={cssForm}
      {...form}
    >
      <div css={cssTextAreaContainer}>
        <FormField
          control={form.control}
          name="content"
          css={cssTextAreaContainer}
          render={({ field }) => (
            <TextArea
              maxLength={BROADCAST_MAX_LENGTH}
              isPersistentPlaceholder={false}
              placeholder={t("dashboard_broadcast_content_placeholder")}
              placeholderCSS={cssPlaceholder}
              css={cssTextArea}
              textProps={{ size: "xl", type: "body" }}
              {...field}
              ref={(element: HTMLTextAreaElement | null) => {
                field.ref(element);
                textAreaRef.current = element;
              }}
            />
          )}
        />
      </div>
    </Form>
  );

  if (isDesktop) {
    return (
      <Dialog.Root open={open} {...props}>
        {!isEditing && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
        <Dialog.Portal>
          <Dialog.Overlay onClick={handleClose} css={cssOverlay} />
          <Dialog.Content css={cssContent}>
            <div css={cssContentContainer}>
              {popoverContainerHost}
              <div css={[cssContentDrawer, drawerOpen && cssContentDrawerOpen]}>
                <div css={[cssHeader, cssHeaderContentDrawer]}>
                  <div css={cssHeaderIconContainer}>
                    <IconButton
                      icon={<ArrowLeftIcon />}
                      onClick={handleToggleDrawerOpen}
                      variant="transparentPrimary"
                    />
                  </div>
                  <Text size="m" css={cssTitle} type="title">
                    <I18nMessage message="dashboard_broadcast_send_to" />
                  </Text>
                  <div css={cssHeaderIconContainer} />
                </div>
                <BroadcastCreateSentToList
                  communityId={communityId}
                  communityIds={communityIds}
                  setCommunityIds={setCommunityIds}
                />
              </div>
              <div css={[cssHeader, cssHeaderContentForm]}>
                <VisuallyHidden>
                  <Dialog.Title>
                    <I18nMessage message="community_detail_groups_screen_title" />
                  </Dialog.Title>
                </VisuallyHidden>
                <Dialog.Close asChild>
                  <IconButton
                    icon={<CloseIcon />}
                    onClick={handleClose}
                    size="s"
                    variant="transparentPrimary"
                  />
                </Dialog.Close>
              </div>
              {broadcastCreateForm}
              <div css={cssFooter}>
                <Text asChild size="l" type="button">
                  <button
                    css={[cssButton, isDisabled && cssButtonDisabled]}
                    onClick={handleGroupChats}
                  >
                    {t("dashboard_broadcast_sending_to_group_chats", {
                      dashboard_broadcast_sending_to_group_chats:
                        communityIds.length,
                    })}
                  </button>
                </Text>
                <div css={cssFooterActions}>
                  {emojiPicker}
                  {scheduleButton}
                  <Button
                    color={colors.purple60}
                    disabled={isDisabled}
                    form="dashboard-broadcast-create-form"
                    size="l"
                    type="submit"
                    variant="color"
                  >
                    <I18nMessage message={submitLabel} />
                  </Button>
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <FullscreenModal
      open={open}
      showCloseButton={false}
      trigger={isEditing ? undefined : trigger}
      contentCSS={cssMobileContent}
      {...props}
    >
      {popoverContainerHost}
      <div
        css={[
          cssMobileSendToDrawerContent,
          drawerOpen && cssMobileSendToDrawerContentOpen,
        ]}
      >
        <div css={cssMobileContentDrawerHeader}>
          <PullDownBar onPullDown={() => setDrawerOpen(false)} />
          <Dialog.Title asChild>
            <Text size="m" css={cssTitle} type="title">
              <I18nMessage message="dashboard_broadcast_send_to" />
            </Text>
          </Dialog.Title>
        </div>

        <ScrollArea
          masksSize="s"
          maskBottom
          maskTop
          masksColor="onOverlayBackground"
          css={cssMobileContentDrawerScrollAreaViewport}
        >
          <BroadcastCreateSentToList
            communityId={communityId}
            communityIds={communityIds}
            setCommunityIds={setCommunityIds}
          />
        </ScrollArea>
        <div css={cssMobileFooter}>
          <Button
            size="l"
            variant="primary"
            onClick={() => setDrawerOpen(false)}
            isFullWidth={true}
          >
            {t("general_word_done")}
          </Button>
        </div>
      </div>

      {!drawerOpen && (
        <>
          <div css={cssMobileHeader}>
            <Dialog.Close asChild>
              <IconButton
                icon={<ArrowLeftIcon />}
                onClick={handleClose}
                size="s"
                variant="transparentPrimary"
              />
            </Dialog.Close>
            <Dialog.Title>
              <Text size="m" type="title">
                <I18nMessage message="dashboard_broadcast" />
              </Text>
            </Dialog.Title>
            <span />
          </div>
          {broadcastCreateForm}
          <div css={cssMobileGroupsListButtonContainer}>
            <Button
              onClick={handleGroupChats}
              size="s"
              startIcon={<EyeIcon.On />}
              variant="transparentPrimary"
              css={cssMobileGroupsListButton}
            >
              {t("dashboard_broadcast_sending_to_group_chats", {
                dashboard_broadcast_sending_to_group_chats: communityIds.length,
              })}
            </Button>
            <div css={cssFooterActions}>
              {emojiPicker}
              {scheduleButton}
            </div>
          </div>
          <Button
            disabled={isDisabled}
            form="dashboard-broadcast-create-form"
            size="l"
            type="submit"
            variant="primary"
            css={cssMobileSubmitButton}
          >
            <I18nMessage message={submitLabel} />
          </Button>
        </>
      )}
    </FullscreenModal>
  );
};
